import { Navigate } from "react-router-dom";

import { AppPagePaths } from "App/routes/paths";
import AIAgentsRoutingPage from "Pages/ai-agents/AIAgentsRoutingPage";
import { lazyWithRetries } from "Utils/LazyWithRetries/LazyWithRetries";

import { AIAgentPaths } from "./paths";

import type { RegalRouteObject } from "App/routes/types";

export type AIAgentsFlags = {
    showCustomAgents: boolean;
    showKnowledgeBase: boolean;
};

const AgentTemplates = lazyWithRetries(
    "Agent Templates",
    () => import("Pages/agentBuilder/Pages/AIAgentTemplatePage/AIAgentTemplatePage")
);
const KnowledgeBasesGridPage = lazyWithRetries(
    "Knowledge Base",
    () => import("Pages/agentBuilder/Pages/KnowledgeBasesGridPage/KnowledgeBasesGridPage")
);
const KnowledgeBaseEditPage = lazyWithRetries(
    "Knowledge Base Edit",
    () => import("Pages/agentBuilder/Pages/KnowledgeBasesGridPage/Pages/KnowledgeBaseEditPage/KnowledgeBaseEditPage")
);
const AgentGridPage = lazyWithRetries(
    "Agent Grid Page",
    () => import("Pages/agentBuilder/Pages/CustomAgentsGridPage/CustomAgentsGridPage")
);

const EditAgent = lazyWithRetries(
    "CustomAgentsWorkspace",
    () => import("Pages/agentBuilder/Pages/CustomAgentsGridPage/Pages/CustomAgentsWorkspace/CustomAgentsWorkspace")
);

const AgentVariantsPage = lazyWithRetries(
    "Agent Variants Page",
    () => import("Pages/agentBuilder/Pages/CustomAgentsGridPage/Pages/AgentVariantsPage/AgentVariantsPage")
);

/**
 * Determines the default redirect path based on available feature flags.
 * Returns the first available page in priority order.
 */
export const getDefaultAIAgentsPath = (flags: AIAgentsFlags): string => {
    const { showCustomAgents, showKnowledgeBase } = flags;

    // Priority: custom agents first (user's work), then knowledge base, then templates (always available)
    if (showCustomAgents) {
        return `${AppPagePaths.aiAgents}/${AIAgentPaths.custom}`;
    }
    if (showKnowledgeBase) {
        return `${AppPagePaths.aiAgents}/${AIAgentPaths.knowledgeBase}`;
    }

    // Fallback to root
    return `/`;
};

export const getAIAgentsRoute = (flags: AIAgentsFlags): RegalRouteObject => {
    const defaultPath = getDefaultAIAgentsPath(flags);

    return {
        path: AppPagePaths.aiAgents,
        element: <AIAgentsRoutingPage />,
        hasNestedRoutes: true,
        children: [
            {
                index: true,
                element: <Navigate to={defaultPath} replace />,
            },
            {
                path: AIAgentPaths.templates,
                element: <AgentTemplates />,
            },
            {
                path: AIAgentPaths.knowledgeBase,
                element: <KnowledgeBasesGridPage />,
            },
            {
                path: AIAgentPaths.kbEdit,
                element: <KnowledgeBaseEditPage />,
            },
            {
                path: AIAgentPaths.custom,
                element: <AgentGridPage />,
            },
            {
                path: AIAgentPaths.agentVariants,
                element: <AgentVariantsPage />,
            },
            {
                path: AIAgentPaths.agentNew,
                element: <EditAgent key="new" />,
            },
            {
                path: AIAgentPaths.agentEdit,
                element: <EditAgent key="edit" />,
            },
            {
                path: AIAgentPaths.agentActions,
                element: <EditAgent key="actions" />,
            },
            {
                path: AIAgentPaths.agentTemplateActions,
                element: <EditAgent key="templateActions" />,
            },
        ],
    };
};
